import type { ComponentChildren } from "preact";
import { useFocusTrap } from "./use-focus-trap";

type ModalProps = {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close (backdrop click or escape) */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Unique ID for the title element (for aria-labelledby) */
  titleId: string;
  /** Modal content */
  children: ComponentChildren;
  /** Optional footer content (e.g., action buttons) */
  footer?: ComponentChildren;
  /** Optional max-width class (default: max-w-sm) */
  maxWidth?: string;
};

/**
 * Reusable modal dialog component.
 * Handles backdrop, focus trap, and escape key.
 */
export function Modal({
  open,
  onClose,
  title,
  titleId,
  children,
  footer,
  maxWidth = "max-w-sm",
}: ModalProps) {
  const containerRef = useFocusTrap<HTMLDivElement>({
    open,
    onEscape: onClose,
  });

  if (!open) return null;

  return (
    <div
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {/* Backdrop */}
      <div
        class="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={containerRef}
        class={`relative w-full ${maxWidth} bg-surface rounded-xl shadow-xl animate-scale-in`}
      >
        <div class="p-6">
          <h3 id={titleId} class="text-lg font-semibold text-text mb-4">
            {title}
          </h3>
          {children}
        </div>
        {footer && <div class="flex gap-3 px-6 pb-6">{footer}</div>}
      </div>
    </div>
  );
}
